/**
 * Two-pane workspace layout hook.
 *
 * Owns the two ResizeObservers that drive the "is there room for both
 * panes side-by-side?" decision:
 *
 *   - ``leftScrollRef`` — catalog scroll container; its measured width
 *     drives container-aware column visibility in the table.
 *   - ``workspaceOuterRef`` — always-visible outer flex parent; its
 *     measured width drives single-pane vs two-pane layout. We observe
 *     the OUTER parent (not the catalog) because the catalog might be
 *     ``display: none`` mid-flow, and a hidden element doesn't fire
 *     ``ResizeObserver`` when the viewport grows back.
 *
 * Mobile detection has two layers:
 *
 *   1. AntD's ``screens.md`` breakpoint — synchronous on first render,
 *      so we get the right layout immediately on phones without a flash.
 *   2. Measured outer width vs ``MIN_TWO_PANE_WORKSPACE_WIDTH`` —
 *      catches in-between sizes (tablet landscape, dragged-narrow
 *      browsers) where the viewport is wide but the catalog area would
 *      still be cramped.
 */

import { useEffect, useRef, useState } from "react";
import { Grid } from "antd";

/** Minimum outer width to support side-by-side: ~300px catalog +
 *  ~360px pane + a bit of breathing room. Below this, fold to
 *  single-pane (catalog hidden, pane full-width). */
export const MIN_TWO_PANE_WORKSPACE_WIDTH = 700;

export interface TwoPaneLayout {
  /** Attach to the inner scroll container (e.g. catalog area). */
  leftScrollRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Attach to the outermost flex parent of the workspace. */
  workspaceOuterRef: React.MutableRefObject<HTMLDivElement | null>;
  /** True when the workspace should collapse to a single pane. */
  isMobile: boolean;
  /** True when the outer width affords a two-pane layout. */
  twoPaneFits: boolean;
  /** Measured catalog scroll container width in px (defaults large
   *  before first observation so all columns show initially). */
  catalogContainerWidth: number;
}

export function useTwoPaneLayout(): TwoPaneLayout {
  const screens = Grid.useBreakpoint();
  const isNarrowViewport = !screens.md;

  const leftScrollRef = useRef<HTMLDivElement | null>(null);
  const [catalogContainerWidth, setCatalogContainerWidth] = useState(9999);
  useEffect(() => {
    const el = leftScrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    setCatalogContainerWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCatalogContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const workspaceOuterRef = useRef<HTMLDivElement | null>(null);
  const [workspaceOuterWidth, setWorkspaceOuterWidth] = useState(9999);
  useEffect(() => {
    const el = workspaceOuterRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    setWorkspaceOuterWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWorkspaceOuterWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const twoPaneFits = workspaceOuterWidth >= MIN_TWO_PANE_WORKSPACE_WIDTH;
  const isMobile = isNarrowViewport || !twoPaneFits;

  return {
    leftScrollRef,
    workspaceOuterRef,
    isMobile,
    twoPaneFits,
    catalogContainerWidth,
  };
}
